import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';
import WizrText from './components/WizrText';
import WizrIcon from './components/WizrIcon';
import AIIcon from './assets/icons/AIIcon';
import { usePermissionValues } from './redux/selectors/content';
import { dispatchFeatureSettings, dispatchSetPermissionValues } from './redux/actions/content';
import CXHubIcon from './assets/icons/CXHubIcon';
import WizrFlexLayout from './components/WizrFlexLayout';
import ControlRoomIcon from './assets/icons/ControlRoomIcon';
import AutoSolveIcon from './assets/icons/AutoSolveIcon';
import { icon } from './icons/svgr.config';
import AgentAssistIcon from './assets/icons/AgentAssistIcon';
import CxSettingsIcon from './assets/icons/CxSettingsIcon';
import KnowledgeBaseIcon from './assets/icons/KnowledgeBaseIcon';
import PastTicketIcon from './assets/icons/PastTicketIcon';
import AutoTagIcon from './assets/icons/AutoTagIcon';
import WorkFlowIcon from './assets/icons/WorkFlowIcon';
import ToolIcon from './assets/icons/ToolIcon';
import NewToolIcon from './assets/icons/NewToolIcon';
import IntegrationsIcon from './assets/icons/IntegrationsIcon';
import InsightsIcon from './assets/icons/InsightsIcon';
import SeekerInteractionSlider from './pages/components/CXControlHubComponents/SeekerInteractionSlider';
import SeekerIcon from './assets/icons/Seeker';
import NewSeekerIcon from './assets/icons/NewSeekerIcon';
import LiveAgentIcon from './assets/icons/LiveAgentIcon';
import AnalyticsHubIcon from './assets/icons/AnalyticsHubIcon';
import AlertsIcon from './assets/icons/AlertsIcon';
import TelemetryIcon from './assets/icons/TelemetryIcon';
import EvalsIcon from './assets/icons/EvalsIcon';

interface AuthContextType {
    userPermission: string | null;
    hasAcces: (role: string) => any;
    filteredMenuItems: any[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Recursively filter menu items and their children based on permissions

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {

    const [userPermission] = useState<string | null>(null);


    // const permissionValues: any = ['view_enterprisehub', 'view_home'] 
    const permissionValues: any = [...usePermissionValues(), 'view_tempEditor'];

    useEffect(() => {
        const appshare = location?.pathname?.includes('/appshare')
        const login = location?.pathname?.includes('/login') || location?.pathname?.includes('/')
        const extension = location?.pathname?.includes('/extension')
        const errorPage = location?.pathname?.includes('./error')

        !appshare && !extension && !login && !errorPage && dispatchFeatureSettings()
    }, [])

    const hasAcces = (permission: string) => {
        return permissionValues.includes(permission)
    };


    function filterMenuItems(items: any[]): any[] {
        return items
            .map(item => {
                if (item.children) {
                    const filteredChildren = filterMenuItems(item.children.filter(Boolean));
                    if (filteredChildren.length > 0) {
                        return { ...item, children: filteredChildren };
                    }
                }
                if (hasAcces(`view_${item.key}`)) {
                    return item;
                }
                return null;
            })
            .filter(Boolean);
    }

    const topMenuItems = [
        {
            key: 'home',
            label: (<a href="/home" onClick={(e) => {e.preventDefault()}}><WizrText type='body2'>Home</WizrText></a>),
            icon: <WizrIcon name='home' />,
            navigateTo: '/dashboard',
        },
        {
            key: 'myworkspace',
            label: (<a href="/myworkspace" onClick={(e) => {e.preventDefault()}}><WizrText type='body2'>My Workspace</WizrText></a>),
            icon: <WizrIcon name='exhub' />,
            navigateTo: '/myworkspace',
        },
        {
            key: 'controlroom',
            label: <WizrText type='body2'>Analytics Hub</WizrText>,
            // label: (<a href="/home" onClick={(e) => {e.preventDefault()}}><WizrText type='body2'>Home</WizrText></a>),
            icon: <AnalyticsHubIcon />,
            children: [
                hasAcces('view_controlroom') && {
                    key: 'controlroom/dashboards',
                    label: (<a href="/controlroom/dashboards" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Control Room</WizrText></a>),
                    icon: <ControlRoomIcon />,
                    navigateTo: '/controlroom/dashboards',
                },
                hasAcces('view_notifications') && {
                    key: 'notifications',
                    label: (<a href="/notifications" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Alerts & Reports</WizrText></a>),
                    icon: <AlertsIcon />,
                    navigateTo: '/notifications',
                },
            ],
        },
        {
            key: 'cxsettings',
            label: <WizrText type='body2'>Support Hub</WizrText>,
            icon: <CXHubIcon />,
            children: [
                hasAcces('view_autosolve') && {
                    key: 'cxsettings/autosolve',
                    label: (<a href="/cxsettings/autosolve" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>AI Agents</WizrText></a>),
                    icon: <AutoSolveIcon />,
                    navigateTo: 'cxsettings/autosolve',
                },
                hasAcces('view_agentassist') && {
                    key: 'cxsettings/agentassist',
                    label: (<a href="/cxsettings/agentassist" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Agent Assist</WizrText></a>),
                    icon: <AgentAssistIcon />,
                    navigateTo: 'cxsettings/agentassist',
                },
                hasAcces('view_autotag') &&
                {
                    key: 'cxsettings/autotag',
                    label: (<a href="/cxsettings/autotag" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Auto Tagging</WizrText></a>),
                    icon: <AutoTagIcon />,
                    navigateTo: 'cxsettings/autotag',
                },
                hasAcces('view_liveagent') && {
                    key: 'liveagent',
                    label: (<a href="/liveagent" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Live Agent Console</WizrText></a>),
                    icon: <LiveAgentIcon />,
                    navigateTo: 'liveagent',
                },
                hasAcces('view_cxsettings') &&
                {
                    key: 'cxsettings',
                    label: (<a href="/cxsettings" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>CX Settings</WizrText></a>),
                    icon: <CxSettingsIcon />,
                    navigateTo: '/cxsettings',
                },
            ],
        },
        {
            key: 'prompt',
            label: <WizrText type='body2'>AI Studio</WizrText>,
            icon: <AIIcon stroke='#999B9F' />,
            children: [
                hasAcces('view_prompt') &&
                {
                    key: 'cxsettings/autosolve',
                    label: (<a href="/cxsettings/autosolve" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>AI Agents</WizrText></a>),
                    icon: <AutoSolveIcon />,
                    navigateTo: 'cxsettings/autosolve',
                },
                hasAcces('view_app-designer') &&
                {
                    key: 'app-designer',
                    label: (<a href="/app-designer" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>AI Workflows</WizrText></a>),
                    icon: <WorkFlowIcon />,
                    navigateTo: '/app-designer',
                },
                hasAcces('view_widgets') &&
                {
                    key: 'cxsettings/widgets',
                    label: (<a href="/cxsettings/widgets" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Tools</WizrText></a>),
                    icon: <NewToolIcon />,
                    navigateTo: 'cxsettings/widgets',
                },
                hasAcces('view_app-designer') &&
                {
                    key: 'integrations',
                    label: (<a href="/integrations" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Integrations</WizrText></a>),
                    icon: <IntegrationsIcon />,
                    navigateTo: '/integrations',
                },
                {
                    key: '/langfuse/datasets',
                    label: <WizrText type='caption2'>Evals</WizrText>,
                    icon: <EvalsIcon />,
                    navigateTo: '/langfuse/datasets',
                },
            ],
        },
        {
            key: 'folder',
            label: <WizrText type='body2'>Content</WizrText>,
            icon: <WizrIcon name='notes' />,
            children: [
                hasAcces('view_folder') &&
                {
                    key: 'folder',
                    label: (<a href="/folder" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Knowledge Base</WizrText></a>),
                    icon: <KnowledgeBaseIcon />,
                    navigateTo: '/folder',
                },
                hasAcces('view_past_tickets') &&
                {
                    key: 'past_tickets',
                    label: (<a href="/past_tickets" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Past Tickets</WizrText></a>),
                    icon: <PastTicketIcon />,
                    navigateTo: '/past_tickets',
                },
            ],
        },
        {
            key: 'customerInteractions',
            label: <WizrText type='body2'>Insights</WizrText>,
            icon: <InsightsIcon />,
            children: [
                hasAcces('view_aiAgentInsights') &&
                {
                    key: 'aiAgentInsights',
                    label: (<a href="/aiAgentInsights" onClick={(e) => {e.preventDefault()}}><WizrFlexLayout flexDirection='row' alignItems='center' background='transparent'>
                            <WizrText type='caption2'>AI Agent Insights</WizrText>
                        </WizrFlexLayout></a>),
                    icon: <AutoSolveIcon />,
                    navigateTo: '/aiAgentInsights',

                },
                hasAcces('view_appInsights') &&
                {
                    key: 'appInsights',
                    label: (<a href="/appInsights" onClick={(e) => {e.preventDefault()}}><WizrFlexLayout flexDirection='row' alignItems='center' background='transparent'>
                        <WizrText type='caption2'>Workflow Insights</WizrText>
                    </WizrFlexLayout></a>),
                    icon: <WorkFlowIcon />,
                    navigateTo: '/appInsights',

                },
                hasAcces('view_agentAssistInsights') &&
                {
                    key: 'agentAssistInsights',
                    label: (<a href="/agentAssistInsights" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Agent Assist Insights</WizrText></a>),
                    icon: <AgentAssistIcon />,
                    navigateTo: '/agentAssistInsights',
                },
                hasAcces('view_seekerInsights') &&
                {
                    key: 'seekerInsights',
                    label: (<a href="/seekerInsights" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Seeker Insights</WizrText></a>),
                    icon: <NewSeekerIcon />,
                    navigateTo: '/seekerInsights',
                },
                {
                    key: 'usageInsights',
                    label: (<a href="/usageInsights" onClick={(e) => {e.preventDefault()}}><WizrText type='caption2'>Usage Insights</WizrText></a>),
                    icon: <AnalyticsHubIcon />,
                    navigateTo: '/usageInsights',
                },
                {
                    key: '/langfuse/traces',
                    label: <WizrText type='caption2'>Telemetry</WizrText>,
                    icon: <TelemetryIcon />,
                    navigateTo: '/langfuse/traces',
                },
            ],            
            // navigateTo: '/folder',
        },
        {
            key: 'settings',
            label: (<a href="/settings" onClick={(e) => {e.preventDefault()}}><WizrText type='body2'>Settings</WizrText></a>),
            icon: <WizrIcon name='settings' />,
            navigateTo: '/settings',
        },
        // ...(tenantDetails?.master_tenant ? [
        //     {
        //         key: 'euserapplisting',
        //         label: <WizrText type='body2'>End User</WizrText>,
        //         icon: <EndUser />,
        //         children: [
        //             {
        //                 key: 'euserapplisting',
        //                 label: <WizrText type='body2'>Content Generation</WizrText>,
        //                 navigateTo: '/euserapplisting',
        //             },
        //             {
        //                 key: 'chatbotlisting',
        //                 label: <WizrText type='body2'>Chat Bot</WizrText>,
        //                 navigateTo: '/chatbotlisting',
        //             },
        //             {
        //                 key: 'playwright',
        //                 label: <WizrText type='body2'>Playwright</WizrText>,
        //                 navigateTo: '/playwright',
        //             },
        //         ],
        //         navigateTo: '/euserapplisting',
        //     },] : []),
    ]

    const filteredMenuItems = topMenuItems.filter(item => {
        if (item.key === 'cxsettings') {
            // Keep if user has view_cxsettings OR view_liveagent
            return hasAcces('view_cxsettings') || hasAcces('view_liveagent');
        }
        if (item.key === 'myworkspace') {
            // Check for view_enterprisehub permission for myworkspace menu
            return hasAcces('view_enterprisehub');
        }
        return hasAcces(`view_${item.key}`);
    });

    useEffect(() => {
        if (localStorage.getItem('id_token')) {
            const idToken: any = localStorage.getItem('id_token');
            dispatchSetPermissionValues(idToken)
        }

    }, [])

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        userPermission,
        hasAcces,
        filteredMenuItems
    }), [userPermission, hasAcces, filteredMenuItems]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};